import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon, Overline, Reveal } from "../components/common";
import { ResponsiveCarousel } from "../components/ResponsiveCarousel";
import { FlyerDownload } from "../components/FlyerDownload";
import { PROGRAMS } from "../data/content";

const ProgramCard = ({ item }) => (
  <div
    data-testid={`program-card-${item.id}`}
    className="group bg-white p-7 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col"
  >
    <div className="h-12 w-12 rounded-xl bg-brand-mist flex items-center justify-center text-brand-navy group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300">
      <Icon name={item.icon} className="h-6 w-6" />
    </div>
    <h3 className="mt-5 text-lg font-semibold font-heading text-brand-navy">
      {item.title}
    </h3>
    <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">
      {item.description}
    </p>
  </div>
);

export default function Programs() {
  const { t } = useTranslation();
  const journeySteps = [
    { n: "01", key: "01" },
    { n: "02", key: "02" },
    { n: "03", key: "03" },
    { n: "04", key: "04" },
  ];
  return (
    <div data-testid="programs-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t("programs.hero.overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05] max-w-4xl lg:max-w-none lg:whitespace-nowrap">
              {t("programs.hero.titleLead")}{" "}
              <br className="hidden lg:block" />
              <span className="text-brand-teal">{t("programs.hero.titleAccent")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-2xl">
              {t("programs.hero.subtitle")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Programs grid / carousel */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResponsiveCarousel
            items={(() => {
              const loc = t("content.programs", { returnObjects: true });
              if (!Array.isArray(loc)) return PROGRAMS;
              return PROGRAMS.map((it, i) => ({ ...it, ...(loc[i] || {}) }));
            })()}
            testId="programs-carousel"
            gridCols="md:grid-cols-2 lg:grid-cols-3"
            renderItem={(item) => <ProgramCard item={item} />}
          />
        </div>
      </section>

      {/* Journey highlight */}
      <section className="py-12 sm:py-16 bg-brand-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl lg:max-w-none">
              <Overline>{t("programs.journey.overline")}</Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy lg:whitespace-nowrap">
                {t("programs.journey.title")}
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {journeySteps.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.08}>
                <div
                  data-testid={`journey-step-${i}`}
                  className="bg-white p-7 rounded-2xl border border-gray-100 shadow-sm h-full"
                >
                  <span className="text-3xl font-bold font-heading text-brand-teal/40">
                    {step.n}
                  </span>
                  <h3 className="mt-3 text-lg font-semibold font-heading text-brand-navy">
                    {t(`programs.journey.steps.${step.key}.t`)}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                    {t(`programs.journey.steps.${step.key}.d`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link
                to="/contact"
                data-testid="programs-contact-cta"
                className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors"
              >
                {t("programs.journey.cta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <FlyerDownload />
    </div>
  );
}
